import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Trophy, ShieldCheck, Target, LineChart, Zap, Clock, Users } from "lucide-react";
import { CountUp } from "@/components/CountUp";
import PaymentMarquee from "@/components/PaymentMarquee";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_STATS = {
  years_experience: 3,
  win_rate: 81.1,
  members: 2500,
  total_tips: 1114,
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    axios
      .get(`${API}/stats`)
      .then(({ data }) => setStats(data))
      .catch(() => setStats(DEFAULT_STATS));
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Professional Asian Handicap Football Tips & Predictions"
        description="Verified Asian Handicap football tips from professional tipsters. 81.1% documented win rate over 1,114+ AH tips, fully transparent public archive, disciplined bankroll staking."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Asian Picks",
          url: "https://asian-picks.com/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://asian-picks.com/predictions?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-ink-950 text-white"
      >
        {/* background image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/35898730/pexels-photo-35898730.jpeg')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/95 via-ink-950/80 to-ink-950" />
        {/* accent glows */}
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-700/30 blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full bg-gold-500/20 blur-[140px]" />
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="noise-overlay" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-32 pb-20 sm:pb-28 z-10">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold-500/40 bg-gold-500/10 text-gold-300 text-xs font-mono uppercase tracking-widest"
            data-testid="hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" />
            Asian Handicap Specialists · Since 2023
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            data-testid="hero-headline"
            className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-5xl"
          >
            Sharp <span className="gold-text">Asian Handicap</span> tips.
            <br className="hidden sm:block" /> Ruthless discipline. Verified edge.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-6 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed"
          >
            We hunt value in the Asian Handicap market — no parlays, no
            longshots, no noise. Every pick is priced between 1.85 and 2.20 and
            logged in a public archive you can audit before you subscribe.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <Link
              to="/pricing"
              data-testid="hero-cta-plans"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gold-500 hover:bg-gold-400 text-ink-950 font-semibold text-base transition-all shadow-xl shadow-gold-500/20 hover:shadow-gold-500/40"
            >
              View Subscription Plans
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/predictions"
              data-testid="hero-cta-archive"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/25 hover:border-gold-400 text-white font-semibold text-base transition-all hover:bg-white/5"
            >
              Previous Subscription Tips
            </Link>
            <Link
              to="/free-tips"
              data-testid="hero-cta-free-tip"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-emerald-500/60 hover:border-emerald-400 text-emerald-300 hover:text-emerald-200 font-semibold text-base transition-all hover:bg-emerald-500/10"
            >
              Daily Free Tip
            </Link>
          </motion.div>

          {/* TRUST INDICATORS */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            data-testid="trust-indicators"
            className="mt-16 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl"
          >
            {[
              { label: "Years of proven tipping", value: stats.years_experience, suffix: "+", icon: Clock, testid: "stat-years" },
              { label: "Verified win rate", value: stats.win_rate, suffix: "%", decimals: 1, icon: Target, testid: "stat-winrate" },
              { label: "Members", value: stats.members, suffix: "+", icon: Users, testid: "stat-members" },
              { label: "Asian Handicap tips delivered", value: stats.total_tips, suffix: "", icon: Trophy, testid: "stat-tips" },
            ].map((s) => (
              <div
                key={s.label}
                data-testid={s.testid}
                className="relative p-5 sm:p-6 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm hover:border-gold-500/40 transition-colors"
              >
                <s.icon className="w-6 h-6 text-emerald-500 mb-4" />
                <div className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
                  <CountUp end={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                </div>
                <div className="mt-2 text-xs sm:text-sm text-white/60 leading-snug">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* fade to body */}
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-ink-50 pointer-events-none" />
      </section>

      {/* WHY US */}
      <section
        data-testid="why-us-section"
        className="py-20 sm:py-28 bg-ink-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-emerald-700 font-mono uppercase tracking-widest text-xs mb-4">
              Why Asian Picks
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-ink-900 tracking-tight">
              A tipping service built the way{" "}
              <span className="text-emerald-700">professionals</span> bet.
            </h2>
            <p className="mt-5 text-ink-500 text-base sm:text-lg leading-relaxed">
              We focus on one market and one market only — the Asian Handicap.
              That focus gives us an edge, and it gives our members the
              consistency that traditional 1X2 tipsters can't offer.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                icon: Target,
                title: "AH-only picks",
                desc: "No exotic bets. No accumulators. Just handicaps in the sweet-spot odds range of 1.85–2.20 where value lives.",
              },
              {
                icon: ShieldCheck,
                title: "Fully transparent record",
                desc: "Every tip we've ever sent is public. Browse the archive, check the settled results, then decide.",
              },
              {
                icon: LineChart,
                title: "Bankroll-first staking",
                desc: "Flat stake advice with each pick. Slow, disciplined growth beats gambler-style chasing every time.",
              },
              {
                icon: Zap,
                title: "Fast, focused delivery",
                desc: "You get one to three quality AH selections per matchday, sent early enough to grab the best line.",
              },
              {
                icon: Trophy,
                title: "3 years, 1,114 tips",
                desc: "A tracked track-record built one settled match at a time. Long enough to trust, current enough to matter.",
              },
              {
                icon: Clock,
                title: "Almost all world leagues",
                desc: "From top-tier European football to South American, Asian and international competitions — we hunt Asian Handicap value wherever it lives.",
              },
            ].map((f) => (
              <div
                key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group p-7 rounded-2xl bg-white border border-ink-200 hover:border-emerald-700/40 hover:shadow-lg hover:shadow-emerald-700/5 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-700/10 flex items-center justify-center mb-5 group-hover:bg-emerald-700 transition-colors">
                  <f.icon className="w-5 h-5 text-emerald-700 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display font-semibold text-lg text-ink-900 mb-2">
                  {f.title}
                </h3>
                <p className="text-sm text-ink-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="bg-white border-y border-ink-200 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="text-emerald-700 font-mono uppercase tracking-widest text-xs mb-2">
              Deposit &amp; withdraw options
            </div>
            <h3 className="font-display font-semibold text-2xl sm:text-3xl text-ink-900">
              Payment methods our members use
            </h3>
          </div>
          <PaymentMarquee />
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="cta-section"
        className="relative overflow-hidden bg-ink-950 text-white py-20 sm:py-28"
      >
        <div className="absolute -top-20 right-1/3 w-[400px] h-[400px] rounded-full bg-emerald-700/30 blur-[120px]" />
        <div className="absolute -bottom-20 left-1/4 w-[400px] h-[400px] rounded-full bg-gold-500/20 blur-[120px]" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Stop guessing. <span className="gold-text">Start following</span> a system.
          </h2>
          <p className="mt-5 text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Pick a package that fits your schedule and bankroll. Receive picks
            straight to your inbox — with clear stake, entry line, and reasoning.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/pricing"
              data-testid="footer-cta-plans"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gold-500 hover:bg-gold-400 text-ink-950 font-semibold transition-all shadow-xl shadow-gold-500/20"
            >
              See the packages
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/predictions"
              data-testid="footer-cta-archive"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/25 hover:border-gold-400 text-white font-semibold hover:bg-white/5 transition-all"
            >
              Audit the archive first
            </Link>
            <Link
              to="/free-tips"
              data-testid="footer-cta-free-tip"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-emerald-500/60 hover:border-emerald-400 text-emerald-300 hover:text-emerald-200 font-semibold hover:bg-emerald-500/10 transition-all"
            >
              Daily Free Tip
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
